import React from 'react';
import Button from '../../../atoms/Button/Button';
import type { ButtonSize, ButtonVariant } from '../../../atoms/Button/Button';
import TooltipCustom from '../../../atoms/Tooltip/Tooltip';
import type { IconName } from '../../../icons/types';

import DialogOrganizeCourse from './DialogOrganizeCourse';
import { useDialogOrganizeCourseButton } from './useDialogOrganizeCourseButton';

/**
 * DialogOrganizeCourseButton - Reusable button component that opens the DialogOrganizeCourse
 *
 * This component encapsulates both the button trigger and the dialog,
 * making it easy to add course organization functionality anywhere in the app.
 */

interface DialogOrganizeCourseButtonProps {
  buttonIcon?: IconName;
  buttonSize?: ButtonSize;
  buttonText?: string;
  buttonVariant?: ButtonVariant;
  className?: string;
  courseGuid: string;
  disabled?: boolean;
  onClose?: () => void;
  programGuid: string;
  showText?: boolean;
}

function DialogOrganizeCourseButton({
  buttonIcon = 'editpost',
  // Match v1: `buttonSize='xs'` + `buttonColor='grey-secondary'` en v2 se
  // corresponden con `size="xs"` (chip 30px) + `variant="neutral-outline"`
  // (bg gris-100, border 1px gris-200, radius 8px). Los defaults previos
  // (`sm` + `secondary-outline`) daban un botón notablemente más grande y
  // con borde 2px, sin equivalencia visual con el v1.
  buttonSize = 'xs',
  buttonText = 'organize-content:Edit program',
  buttonVariant = 'neutral-outline',
  className,
  courseGuid,
  disabled = false,
  onClose,
  programGuid,
  showText = true,
}: DialogOrganizeCourseButtonProps): JSX.Element {
  const { t, isOpen, openDialog, closeDialog } = useDialogOrganizeCourseButton();

  function handleClose(): void {
    closeDialog();
    onClose?.();
  }

  const button = (
    <Button
      ariaLabel={showText ? undefined : t(buttonText)}
      className={className}
      isDisabled={disabled}
      isIconOnly={!showText}
      leftIcon={buttonIcon}
      iconSize="md"
      onClick={openDialog}
      size={buttonSize}
      text={showText ? t(buttonText) : undefined}
      variant={showText ? buttonVariant : 'secondary-ghost'}
    />
  );

  return (
    <>
      {showText ? (
        button
      ) : (
        <TooltipCustom ariaMode="labelledby" title={t(buttonText)}>
          {button}
        </TooltipCustom>
      )}
      {isOpen && <DialogOrganizeCourse courseGuid={courseGuid} programGuid={programGuid} open={isOpen} onClose={handleClose} />}
    </>
  );
}

export default DialogOrganizeCourseButton;
